import React, { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import { toast } from 'react-hot-toast';
import { SketchPicker } from 'react-color';
import { CREATE_WORK_ORDER_FLAG_TEMPLATE, UPDATE_WORK_ORDER_FLAG_TEMPLATE } from '../../graphql/work-order-flags';
import { FlagIcon, getIconNames } from '../../utils/flag-icons';
import { calculateTextColor, calculateTintedFlagTextColor } from '../../utils/custom-flag-utils';

const FLAG_COLORS = [
  { name: 'Red', value: '#ef4444' },
  { name: 'Orange', value: '#f97316' },
  { name: 'Yellow', value: '#eab308' },
  { name: 'Green', value: '#22c55e' },
  { name: 'Blue', value: '#3b82f6' },
  { name: 'Purple', value: '#a855f7' },
  { name: 'Pink', value: '#ec4899' },
  { name: 'Gray', value: '#6b7280' }
];

function WorkOrderFlagTemplateEditor({ template, onClose, onSave }) {
  const [formData, setFormData] = useState({
    flagValue: '',
    iconName: 'tag',
    backgroundColor: '#3b82f6',
    textColor: '#ffffff'
  });
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showIconPicker, setShowIconPicker] = useState(false);

  const [createTemplate, { loading: creating }] = useMutation(CREATE_WORK_ORDER_FLAG_TEMPLATE);
  const [updateTemplate, { loading: updating }] = useMutation(UPDATE_WORK_ORDER_FLAG_TEMPLATE);

  const isEditing = !!template;
  const isLoading = creating || updating;
  const iconNames = getIconNames();

  useEffect(() => {
    if (template) {
      setFormData({
        flagValue: template.flagValue || '',
        iconName: template.iconName || 'tag',
        backgroundColor: template.backgroundColor || '#3b82f6',
        textColor: template.textColor || calculateTextColor(template.backgroundColor || '#3b82f6')
      });
    }
  }, [template]);

  const handleColorChange = (color) => {
    setFormData(prev => ({
      ...prev,
      backgroundColor: color.hex,
      textColor: calculateTextColor(color.hex)
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formData.flagValue.trim()) {
      toast.error('Flag text is required');
      return;
    }

    const input = {
      flagValue: formData.flagValue.trim(),
      iconName: formData.iconName,
      backgroundColor: formData.backgroundColor,
      textColor: formData.textColor,
      sortOrder: template?.sortOrder || 0
    };

    try {
      if (isEditing) {
        await updateTemplate({ variables: { input: { id: template.id, ...input } } });
        toast.success('Work order flag updated');
      } else {
        await createTemplate({ variables: { input } });
        toast.success('Work order flag created');
      }
      onSave?.();
      onClose?.();
    } catch (error) {
      toast.error(error.message || 'Failed to save work order flag');
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {isEditing ? 'Edit Work Order Flag' : 'Create Work Order Flag'}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4">
          <div className="mb-4">
            <label htmlFor="workOrderFlagValue" className="block text-sm font-medium text-gray-700 mb-1">
              Flag Text <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="workOrderFlagValue"
              value={formData.flagValue}
              onChange={(event) => setFormData(prev => ({ ...prev, flagValue: event.target.value }))}
              maxLength={50}
              required
              className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., Parts Needed, Vendor Required, Safety"
            />
            <p className="mt-1 text-xs text-gray-500">{formData.flagValue.length}/50 characters</p>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Icon <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowIconPicker(!showIconPicker)}
                className="w-full flex items-center justify-between border border-gray-300 rounded-md px-3 py-2 bg-white hover:bg-gray-50"
              >
                <div className="flex items-center">
                  <FlagIcon iconName={formData.iconName} className="h-5 w-5 mr-2" />
                  <span className="text-sm text-gray-700 capitalize">{formData.iconName}</span>
                </div>
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showIconPicker && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-64 overflow-y-auto">
                  <div className="grid grid-cols-4 gap-2 p-3">
                    {iconNames.map(iconName => (
                      <button
                        key={iconName}
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({ ...prev, iconName }));
                          setShowIconPicker(false);
                        }}
                        className={`p-3 rounded-md border-2 hover:border-blue-500 transition-colors ${
                          formData.iconName === iconName ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                        }`}
                        title={iconName}
                      >
                        <FlagIcon iconName={iconName} className="h-6 w-6 mx-auto" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Background Color <span className="text-red-500">*</span>
            </label>
            <div className="flex items-center space-x-2">
              <div className="flex flex-wrap gap-2 flex-1">
                {FLAG_COLORS.map(color => (
                  <button
                    key={color.value}
                    type="button"
                    onClick={() => handleColorChange({ hex: color.value })}
                    className={`w-10 h-10 rounded-md border-2 ${
                      formData.backgroundColor === color.value
                        ? 'border-gray-900 ring-2 ring-offset-2 ring-blue-500'
                        : 'border-gray-300'
                    }`}
                    style={{ backgroundColor: color.value }}
                    title={color.name}
                  />
                ))}
              </div>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowColorPicker(!showColorPicker)}
                  className="w-12 h-12 rounded-md border-2 border-gray-300"
                  style={{ backgroundColor: formData.backgroundColor }}
                  title="Custom color"
                />
                {showColorPicker && (
                  <div className="absolute right-0 mt-2 z-10">
                    <SketchPicker color={formData.backgroundColor} onChange={handleColorChange} disableAlpha />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <label className="block text-sm font-medium text-gray-700 mb-2">Preview</label>
            <div
              className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium"
              style={{
                backgroundColor: `${formData.backgroundColor}20`,
              color: calculateTintedFlagTextColor(formData.backgroundColor, 0.125),
                border: `1px solid ${formData.backgroundColor}40`
              }}
            >
              <FlagIcon iconName={formData.iconName} className="h-4 w-4 mr-1" />
              <span>{formData.flagValue || 'Flag Text'}</span>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {isLoading ? 'Saving...' : isEditing ? 'Update Flag' : 'Create Flag'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default WorkOrderFlagTemplateEditor;
